<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { worldStore } from '@sim/store';
  import { SIM, roomDef, zoneOf, ZONE_META, isNight } from '@sim/config';
  import { slotX, roomAt, activeBuilding } from '@sim/world';
  import { roomView } from '@sim/selectors';
  import ColonistSprite from '@ui-sim/ColonistSprite.svelte';
  import RoomScene from '@ui-sim/RoomScene.svelte';
  import { music } from '@ui-sim/music';
  import { isRainy } from '@ui-sim/weather';
  import type { Colonist } from '@sim/types';

  export let selectedColonist: number | null = null;
  export let selectedRoom: number | null = null;

  const emit = createEventDispatcher();
  // Fixed floor scale (genre convention — Fallout Shelter et al. don't inflate
  // tile size to fill the window). Leftover vertical space on big screens goes
  // to the sky/earth atmosphere bands instead (see .towerhost/.sky/.earth below).
  const FLOOR_H = 96;

  $: city = $worldStore;
  $: b = activeBuilding(city)!;
  $: floorList = Array.from({ length: b.floorMax - b.floorMin + 1 }, (_, i) => b.floorMax - i); // top → bottom
  $: cellW = (0.76 / SIM.SLOTS_PER_FLOOR) * 100;
  $: night = isNight(city.t);
  $: rain = isRainy(city.t);

  // ---- elevator: riders are carried by the cab, not drawn as pawns -------
  // While changing floors the sim parks a colonist at x≈0 and lerps a
  // FRACTIONAL floor — perfect to drive the cab and hide the sprite.
  const isRiding = (c: Colonist) => c.x <= 0.05 && Math.abs(c.floor - c.targetFloor) >= 0.05;
  let cabParkFloor = 0;
  let prevRiding = false;
  $: rider = b.colonists.find(isRiding);
  $: if (rider) cabParkFloor = rider.targetFloor;
  $: cabTop = floorTop(rider ? rider.floor : Math.max(b.floorMin, Math.min(b.floorMax, cabParkFloor))) + 16;
  // Only riders travelling WITH the cab vanish into it; anyone else in the
  // shaft stays visible gliding, so nobody pops out of thin air on arrival.
  $: hiddenRiders = new Set(
    rider ? b.colonists.filter((c) => isRiding(c) && Math.abs(c.floor - rider!.floor) < 0.45).map((c) => c.id) : []
  );
  $: {
    const r = !!rider;
    if (prevRiding && !r) music.ding(); // arrival
    prevRiding = r;
  }

  // ---- pawns snap to furniture stations while using a room ----------------
  type Station = { dx: number; dy: number; pose?: 'sit' | 'stand' };
  // dy = feet height as a fraction of FLOOR_H; rooms now end 25px above the
  // slab (label plaque below), so the scene floor line sits near 0.66.
  const WORK_ST: Record<string, Station[]> = {
    grow: [{ dx: 0.3, dy: 0.68 }, { dx: 0.62, dy: 0.68 }],
    deal: [{ dx: 0.28, dy: 0.68 }, { dx: 0.52, dy: 0.68 }],
    lab: [{ dx: 0.3, dy: 0.68 }, { dx: 0.56, dy: 0.68 }],
    pack: [{ dx: 0.32, dy: 0.68 }, { dx: 0.62, dy: 0.68 }],
    security: [{ dx: 0.72, dy: 0.68 }, { dx: 0.45, dy: 0.68 }],
    diner: [{ dx: 0.2, dy: 0.68 }],
  };
  // anchored at each bed's centre — the lying sprite spans the mattress with
  // its head on the pillow (left end)
  const BED_ST: Station[] = [
    { dx: 0.28, dy: 0.54 }, { dx: 0.72, dy: 0.54 },
    { dx: 0.28, dy: 0.34 }, { dx: 0.72, dy: 0.34 },
  ];
  const EAT_ST: Station[] = [
    { dx: 0.63, dy: 0.56, pose: 'sit' }, { dx: 0.79, dy: 0.56, pose: 'sit' }, { dx: 0.45, dy: 0.68 },
  ];
  const RELAX_ST: Station[] = [
    { dx: 0.2, dy: 0.56, pose: 'sit' }, { dx: 0.34, dy: 0.56, pose: 'sit' }, { dx: 0.87, dy: 0.68 },
  ];
  function stationFor(c: Colonist): { left: number; top: number; pose?: 'sit' | 'stand' } | null {
    if (c.targetRoomId == null) return null;
    const act = c.activity;
    if (act !== 'work' && act !== 'sleep' && act !== 'eat' && act !== 'relax' && act !== 'recover') return null;
    const room = b.rooms.find((r) => r.id === c.targetRoomId);
    if (!room) return null;
    const list =
      act === 'sleep' || act === 'recover' ? BED_ST
      : act === 'eat' ? EAT_ST
      : act === 'relax' ? RELAX_ST
      : WORK_ST[room.type] ?? [{ dx: 0.5, dy: 0.84 }];
    // sleep + recover share the same berth pool (both occupy beds)
    const grp = (a: string) => (a === 'sleep' || a === 'recover' ? 'bed' : a);
    const mates = b.colonists.filter((o) => o.targetRoomId === room.id && grp(o.activity) === grp(act)).sort((a, z) => a.id - z.id);
    const st = list[Math.max(0, mates.findIndex((o) => o.id === c.id)) % list.length];
    return {
      left: (slotX(room.slot) - cellW / 200 + st.dx * (cellW / 100)) * 100,
      top: floorTop(room.floor) + FLOOR_H * st.dy,
      pose: st.pose,
    };
  }

  // ---- thought bubbles -----------------------------------------------------
  function bubbleFor(c: Colonist): string | null {
    if (c.activity === 'sleep' || c.activity === 'recover') return null; // in bed = already handled, the z says it
    if (c.needs.rest < SIM.CRITICAL || c.needs.food < SIM.CRITICAL || c.needs.joy < SIM.CRITICAL) return '😰';
    if (c.mood < 25) return '💢';
    if (c.activity === 'eat') return '🍜';
    if (c.activity === 'relax') return '🎮';
    return null;
  }

  // ---- construction moment: scaffold + wipe-in on freshly built rooms ------
  let knownRooms = new Set<number>();
  let knownBid = -1;
  let freshRooms = new Set<number>();
  $: {
    const ids = new Set(b.rooms.map((r) => r.id));
    if (b.id === knownBid) {
      for (const id of ids) {
        if (!knownRooms.has(id)) {
          freshRooms.add(id);
          freshRooms = freshRooms;
          music.hammer();
          setTimeout(() => {
            freshRooms.delete(id);
            freshRooms = freshRooms;
          }, 2600);
        }
      }
    }
    knownRooms = ids;
    knownBid = b.id;
  }

  // ---- register cha-ching as product moves on the street -------------------
  let prevProd = -1;
  let prevProdBid = -1;
  let soldAcc = 0;
  $: {
    if (b.id === prevProdBid && prevProd >= 0) {
      const d = prevProd - b.res.product;
      if (d > 0) {
        soldAcc += d;
        if (soldAcc >= 6) {
          soldAcc = 0;
          music.chaChing();
        }
      }
    }
    prevProd = b.res.product;
    prevProdBid = b.id;
  }

  function floorTop(floor: number): number {
    return (b.floorMax - floor) * FLOOR_H;
  }
  function floorTag(floor: number): string {
    if (floor === 0) return 'ST';
    if (floor < 0) return 'B' + -floor;
    if (floor === b.floorMax) return 'PH';
    return '' + floor;
  }
</script>

<div class="towerhost">
<div class="bwrap" class:night class:rain>
  <!-- sky + roofline above the top floor -->
  <div class="sky">
    <div class="stars"></div>
    <div class="sun"></div>
    <div class="moon"></div>
    <div class="rainveil"></div>
    <div class="flash"></div>
    <div class="nb nb-l"></div>
    <div class="nb nb-r"></div>
    <svg class="roofprops" viewBox="0 0 400 30" preserveAspectRatio="xMidYMax meet" aria-hidden="true">
      <!-- water tank -->
      <line x1="52" y1="16" x2="52" y2="30" stroke="#101018" stroke-width="2.4" />
      <line x1="72" y1="16" x2="72" y2="30" stroke="#101018" stroke-width="2.4" />
      <rect x="46" y="6" width="32" height="14" rx="2" fill="#181822" />
      <path d="M44 7 L62 -2 L80 7 Z" fill="#131320" />
      <!-- AC units -->
      <rect x="196" y="18" width="22" height="12" rx="1.5" fill="#161620" />
      <rect x="224" y="21" width="16" height="9" rx="1.5" fill="#13131d" />
      <!-- antenna -->
      <line x1="330" y1="30" x2="330" y2="2" stroke="#181822" stroke-width="2" />
      <line x1="322" y1="10" x2="338" y2="10" stroke="#181822" stroke-width="1.4" />
      <circle class="beacon" cx="330" cy="2" r="1.8" fill="#ff5470" />
    </svg>
    <div class="parapet"></div>
  </div>

  <div class="tower" style="height:{(b.floorMax - b.floorMin + 1) * FLOOR_H}px">
    <div class="shaft">
      <div class="rail"></div>
      <div
        class="cab"
        style="top:{cabTop}px; height:{FLOOR_H - 32}px; transition: top {rider ? '0.12s linear' : '0.7s cubic-bezier(0.45, 0, 0.25, 1)'}"
      >
        <div class="cabslit"></div>
      </div>
    </div>
    <div class="wall"></div>

    {#each floorList as f (f)}
      {@const zone = zoneOf(f, b.floorMax)}
      {@const zm = ZONE_META[zone]}
      <div class="floor" class:street={f === 0} style="top:{floorTop(f)}px; height:{FLOOR_H}px; background:{zm.tint}">
        <div class="fnum" style="color:{zm.accent}">{floorTag(f)}</div>
        <span class="fwin"></span>

        {#each Array(SIM.SLOTS_PER_FLOOR) as _, slot (slot)}
          {@const room = roomAt(b, f, slot)}
          {#if room}
            {@const def = roomDef(room.type)}
            {@const rv = roomView(b, room, city.t)}
            {@const staffed = rv.workers.length > 0}
            <button
              class="room"
              class:sel={selectedRoom === room.id}
              class:damaged={rv.damaged}
              class:lit={night && !rv.damaged}
              class:fresh={freshRooms.has(room.id)}
              style="left:{slotX(slot) * 100}%; width:{cellW}%; --accent:{def.accent}; --glow:{def.glow}"
              on:click={() => emit('selectRoom', room.id)}
            >
              <RoomScene type={room.type} {staffed} {night} {rain} {zone} tier={room.tier} stock={b.res.product} meals={b.res.meals} />
              {#if freshRooms.has(room.id)}
                <span class="scaffold"></span>
                <span class="dust d1"></span>
                <span class="dust d2"></span>
              {/if}
              {#if rv.assignable}
                <span class="rbar" class:live={staffed}><span class="rfill" style="width:{room.progress * 100}%"></span></span>
              {/if}
              {#if rv.damaged}
                <span class="boards"></span>
                <span class="smoke"></span>
                <span class="dmg">🔧 {Math.ceil(rv.damageLeft)}s</span>
              {/if}
            </button>
            <!-- name + staffing on a plaque below the room, off the artwork -->
            <button
              class="rlabel"
              class:sel={selectedRoom === room.id}
              style="left:{slotX(slot) * 100}%; width:{cellW}%; --accent:{def.accent}"
              on:click={() => emit('selectRoom', room.id)}
            >
              <span class="rlname"><span class="ricon">{def.icon}</span>{def.name}</span>
              {#if rv.assignable}
                <span class="rldot">·</span>
                <span class="rlcount"><span class="rlicon">👥</span>{rv.workers.length}/{rv.capacity}</span>
              {/if}
            </button>
          {:else}
            <button
              class="slot"
              style="left:{slotX(slot) * 100}%; width:{cellW}%"
              on:click={() => emit('buildAt', { floor: f, slot })}
              aria-label="Build here"
            >＋</button>
          {/if}
        {/each}
      </div>
    {/each}

    <!-- street ground line between floor 0 and the basement -->
    <div class="ground" style="top:{floorTop(0) + FLOOR_H}px"></div>

    {#each b.colonists as c (c.id)}
      {#if c.activity !== 'arrested' && !hiddenRiders.has(c.id)}
        {@const st = stationFor(c)}
        {@const bubble = bubbleFor(c)}
        <div
          class="pawnpos"
          class:stationed={!!st}
          style="left:{st ? st.left : c.x * 100}%; top:{st ? st.top : floorTop(c.floor) + FLOOR_H * 0.74}px"
        >
          <ColonistSprite {c} selected={selectedColonist === c.id} seated={st?.pose === 'sit'} onSelect={(id) => emit('selectColonist', id)} />
          {#if bubble}
            <span class="bubble">{bubble}</span>
          {:else if c.activity === 'idle'}
            <span class="bubble chat" style="animation-delay:-{(c.id % 7) * 1.9}s">💬</span>
          {/if}
        </div>
      {/if}
    {/each}
  </div>

  <!-- earth below the deepest basement -->
  <div class="earth">
    <div class="pipe"></div>
  </div>
</div>
</div>

<style>
  /* Fills whatever height the stage gives us (see AppSim.svelte .stagewrap /
     main). Floor scale never changes (see FLOOR_H) — instead the sky/earth
     atmosphere bands (flex-grow, below) absorb the leftover space on a big
     screen, and this scrolls as a fallback when a tall building doesn't fit. */
  .towerhost {
    flex: 1;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
  .bwrap {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    margin: 6px 4px 10px;
    border-radius: 12px;
    border: 1px solid var(--line);
    background: #0c0c14;
    overflow: hidden;
  }

  /* ---- sky + roofline -------------------------------------------------- */
  .sky {
    position: relative;
    flex: 2 1 66px;
    min-height: 66px;
    background: linear-gradient(180deg, #35466b 0%, #6b4a44 78%, #8a5a40 100%);
    overflow: hidden;
  }
  .night .sky {
    background: linear-gradient(180deg, #070a16 0%, #131028 78%, #1c1430 100%);
  }
  .stars {
    position: absolute;
    top: 6px;
    left: 10px;
    width: 1px;
    height: 1px;
    border-radius: 50%;
    background: #fff;
    box-shadow:
      34px 4px #fff, 78px 12px #cfd8ff, 130px 2px #fff, 168px 16px #cfd8ff, 214px 7px #fff,
      262px 13px #fff, 305px 3px #cfd8ff, 348px 10px #fff, 388px 5px #cfd8ff, 105px 20px #fff;
    opacity: 0;
    transition: opacity 0.8s ease;
  }
  .night .stars {
    opacity: 0.85;
  }
  .sun,
  .moon {
    position: absolute;
    top: 10px;
    right: 14%;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    transition: opacity 0.8s ease;
  }
  .sun {
    background: radial-gradient(circle, #ffd9a0 0%, #f0a860 55%, transparent 72%);
    box-shadow: 0 0 18px 6px rgba(240, 168, 96, 0.4);
  }
  .moon {
    background: radial-gradient(circle at 62% 40%, #e8ecf5 55%, #b9c2d4 70%, transparent 72%);
    box-shadow: 0 0 12px 2px rgba(220, 230, 255, 0.35);
    opacity: 0;
  }
  .night .sun { opacity: 0; }
  .night .moon { opacity: 1; }
  /* neighbouring rooftops peeking in at the edges */
  .nb {
    position: absolute;
    bottom: 7px;
    width: 44px;
    height: 34px;
    /* dark mullion columns cut the lit bands into window dots, not stripes */
    background:
      repeating-linear-gradient(90deg, #10101a 0 4px, transparent 4px 12px),
      repeating-linear-gradient(0deg, transparent 0 7px, rgba(255, 216, 140, 0.08) 7px 10px),
      #10101a;
    opacity: 0.9;
  }
  .nb-l { left: -6px; height: 26px; }
  .nb-r { right: -6px; }
  .night .nb {
    background:
      repeating-linear-gradient(90deg, #0a0a12 0 4px, transparent 4px 12px),
      repeating-linear-gradient(0deg, transparent 0 7px, rgba(255, 216, 140, 0.22) 7px 10px),
      #0a0a12;
  }
  .roofprops {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 7px;
    width: 100%;
    height: 30px;
  }
  .beacon {
    opacity: 0.25;
  }
  .night .beacon {
    animation: beacon 2.4s ease-in-out infinite;
  }
  @keyframes beacon {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
  .parapet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 7px;
    background: linear-gradient(180deg, #2c2c3e 0 2px, #1d1d2a 2px);
  }
  /* ---- weather ---------------------------------------------------------- */
  .rainveil,
  .flash {
    position: absolute;
    inset: 0;
    opacity: 0;
    pointer-events: none;
  }
  .rainveil {
    background: repeating-linear-gradient(103deg, transparent 0 6px, rgba(165, 185, 215, 0.16) 6px 7px);
    animation: rainshift 0.5s linear infinite paused;
  }
  .rain .rainveil {
    opacity: 1;
    animation-play-state: running;
  }
  .rain .sun {
    opacity: 0.35;
  }
  .rain .sky {
    filter: brightness(0.8) saturate(0.8);
  }
  .flash {
    background: rgba(220, 230, 255, 0.85);
  }
  .rain .flash {
    animation: lightning 16s linear infinite;
  }
  @keyframes rainshift {
    from { background-position: 0 0; }
    to { background-position: -8px 28px; }
  }
  @keyframes lightning {
    0%, 41.5%, 43.5%, 100% { opacity: 0; }
    42% { opacity: 0.55; }
    42.6% { opacity: 0.1; }
    43% { opacity: 0.35; }
  }

  /* ---- the tower itself ------------------------------------------------- */
  .tower {
    position: relative;
    flex: 0 0 auto;
    background: linear-gradient(180deg, #12121c, #0c0c14);
    transition: filter 0.8s ease;
  }
  .night .tower {
    filter: brightness(0.86) saturate(0.94);
  }
  .shaft {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 14%;
    background: linear-gradient(180deg, #07070d, #0a0a12);
    border-right: 1px solid #000;
    z-index: 1;
  }
  .rail {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    transform: translateX(-50%);
    background: repeating-linear-gradient(180deg, #2a2a3a 0 7px, transparent 7px 14px);
  }
  .cab {
    position: absolute;
    left: 18%;
    width: 64%;
    border-radius: 4px;
    background: linear-gradient(180deg, #262633, #16161f);
    border: 1px solid #33334a;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.6);
    transition: top 0.7s cubic-bezier(0.45, 0, 0.25, 1);
    z-index: 2;
  }
  .cab::before {
    /* cable up to the roof (clipped by the shaft) */
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    width: 2px;
    height: 2000px;
    transform: translateX(-50%);
    background: #1d1d29;
  }
  .cab::after {
    /* door seam */
    content: '';
    position: absolute;
    top: 12%;
    bottom: 8%;
    left: 50%;
    width: 1.5px;
    transform: translateX(-50%);
    background: #0b0b12;
  }
  .cabslit {
    position: absolute;
    top: 22%;
    left: 22%;
    right: 22%;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 216, 140, 0.5);
    box-shadow: 0 0 6px rgba(255, 216, 140, 0.45);
  }
  /* brick wall strip + windows on the right edge */
  .wall {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 7.5%;
    background:
      repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.28) 0 3px, transparent 3px 9px),
      #191420;
    border-left: 1px solid #000;
    z-index: 1;
  }
  .fwin {
    position: absolute;
    right: 2.2%;
    width: 3.2%;
    top: 26%;
    height: 38%;
    border-radius: 2px;
    background: #0e1420;
    border: 1px solid #262c3c;
    z-index: 2;
    transition: background 0.8s ease, box-shadow 0.8s ease;
  }
  .night .fwin {
    background: rgba(255, 213, 122, 0.75);
    box-shadow: 0 0 9px rgba(255, 200, 110, 0.5);
  }
  .floor {
    position: absolute;
    left: 0;
    width: 100%;
  }
  /* concrete slab between floors (replaces the old 1px hairline) */
  .floor::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 5px;
    background: linear-gradient(180deg, #23232f, #12121c);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.55);
  }
  .floor.street {
    box-shadow: inset 0 -2px 0 rgba(255, 84, 112, 0.3);
  }
  .ground {
    position: absolute;
    left: 0;
    width: 100%;
    height: 0;
    border-top: 2px dashed rgba(255, 213, 74, 0.35);
    z-index: 1;
  }
  .fnum {
    position: absolute;
    left: 3px;
    top: 4px;
    z-index: 2;
    font-size: 9px;
    font-weight: 700;
    font-family: var(--mono);
    opacity: 0.85;
  }

  /* ---- rooms: diorama + label chip -------------------------------------- */
  .room {
    position: absolute;
    top: 7px;
    bottom: 25px;
    transform: translateX(-50%);
    padding: 0;
    border-radius: 9px;
    background: #10101a;
    border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--line));
    box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.5);
    color: var(--ink);
    overflow: hidden;
    z-index: 2;
  }
  .room.sel {
    box-shadow: 0 0 0 2px var(--accent), 0 0 14px var(--glow), inset 0 2px 8px rgba(0, 0, 0, 0.5);
  }
  .room.lit {
    box-shadow: inset 0 -14px 26px -14px rgba(255, 190, 110, 0.28), inset 0 2px 8px rgba(0, 0, 0, 0.5);
  }
  .room.damaged {
    filter: grayscale(0.55) brightness(0.75);
  }
  /* plaque strip below the room: name left, staffing right */
  .rlabel {
    position: absolute;
    bottom: 7px;
    height: 15px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 0 6px;
    border-radius: 5px;
    background: rgba(9, 10, 16, 0.78);
    border: 1px solid rgba(255, 255, 255, 0.08);
    color: var(--ink-dim);
    z-index: 2;
    white-space: nowrap;
    overflow: hidden;
  }
  .rldot {
    color: var(--ink-faint);
    font-size: 8.5px;
    font-weight: 700;
    line-height: 1;
  }
  .rlabel.sel {
    border-color: var(--accent);
    box-shadow: 0 0 8px color-mix(in srgb, var(--accent) 45%, transparent);
  }
  .rlname {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 8.5px;
    font-weight: 700;
    line-height: 1;
  }
  .ricon {
    font-size: 9.5px;
    line-height: 1;
  }
  /* same typeface + size as the name so the digits share its exact baseline */
  .rlcount {
    display: inline-flex;
    align-items: center;
    gap: 2.5px;
    font-size: 8.5px;
    font-weight: 700;
    line-height: 1;
    color: var(--accent);
  }
  .rlicon {
    font-size: 8px;
    line-height: 1;
  }
  .rbar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: rgba(0, 0, 0, 0.75);
    overflow: hidden;
    z-index: 3;
  }
  .rfill {
    display: block;
    height: 100%;
    background: var(--accent);
    box-shadow: 0 0 6px var(--glow);
  }
  .rbar.live .rfill {
    animation: barpulse 1.8s ease-in-out infinite;
  }
  @keyframes barpulse {
    0%, 100% { filter: brightness(1); }
    50% { filter: brightness(1.5); }
  }
  /* damage: nailed boards + smoke */
  .boards {
    position: absolute;
    inset: 0;
    z-index: 4;
    border-radius: inherit;
    background:
      linear-gradient(9deg, transparent 40%, #57402a 40.5%, #6a4e32 47%, #57402a 53.5%, transparent 54%),
      linear-gradient(-12deg, transparent 58%, #4a3623 58.5%, #5d4530 65%, #4a3623 71.5%, transparent 72%);
    pointer-events: none;
  }
  .smoke {
    position: absolute;
    left: 30%;
    top: 30%;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(150, 150, 160, 0.5), transparent 70%);
    z-index: 4;
    animation: smoke 3s ease-out infinite;
    pointer-events: none;
  }
  @keyframes smoke {
    0% { opacity: 0; transform: translateY(4px) scale(0.6); }
    30% { opacity: 0.7; }
    100% { opacity: 0; transform: translateY(-16px) scale(1.5); }
  }
  .dmg {
    position: absolute;
    left: 50%;
    top: 40%;
    transform: translate(-50%, -50%);
    z-index: 5;
    font-size: 9px;
    font-weight: 800;
    color: var(--bad);
    text-shadow: 0 1px 3px #000;
  }

  /* ---- empty slots: raw construction framing ---------------------------- */
  .slot {
    position: absolute;
    top: 12px;
    bottom: 25px;
    transform: translateX(-50%);
    border-radius: 9px;
    border: 1px solid #222230;
    background:
      linear-gradient(180deg, #262636 0 3px, transparent 3px),
      repeating-linear-gradient(90deg, #1d1d2a 0 2.5px, rgba(255, 255, 255, 0.014) 2.5px 17px);
    color: transparent;
    font-size: 18px;
    z-index: 2;
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .slot::before {
    /* bare bulb on a wire */
    content: '';
    position: absolute;
    top: 3px;
    left: 50%;
    width: 1.5px;
    height: 9px;
    background: #33334a;
  }
  .slot::after {
    content: '';
    position: absolute;
    top: 12px;
    left: 50%;
    width: 5px;
    height: 5px;
    transform: translateX(-33%);
    border-radius: 50%;
    background: rgba(255, 216, 140, 0.5);
    box-shadow: 0 0 5px rgba(255, 216, 140, 0.4);
  }
  .slot:hover {
    border-color: var(--good);
    color: var(--ink);
    background:
      linear-gradient(180deg, #262636 0 3px, rgba(57, 224, 112, 0.05) 3px),
      repeating-linear-gradient(90deg, #1d1d2a 0 2.5px, rgba(255, 255, 255, 0.02) 2.5px 17px);
  }

  /* ---- earth below the basement ----------------------------------------- */
  .earth {
    position: relative;
    flex: 1 1 44px;
    min-height: 44px;
    background:
      radial-gradient(3px 2px at 14% 30%, #3a2c1c 98%, transparent),
      radial-gradient(4px 2.5px at 38% 66%, #33261a 98%, transparent),
      radial-gradient(3px 2px at 62% 34%, #3a2c1c 98%, transparent),
      radial-gradient(4px 2.5px at 84% 58%, #33261a 98%, transparent),
      linear-gradient(180deg, #221a10 0%, #140e08 100%);
  }
  .pipe {
    position: absolute;
    left: 0;
    right: 0;
    top: 15px;
    height: 7px;
    background:
      repeating-linear-gradient(90deg, transparent 0 64px, #4a505c 64px 69px),
      linear-gradient(180deg, #3a3f4a 0 2px, #262b34 2px 5px, #191d24 5px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }

  .pawnpos {
    position: absolute;
    transform: translate(-50%, -100%);
    transition: left 0.12s linear, top 0.12s linear;
    z-index: 4;
  }
  /* settling into a station (bed/stool/bench) is a gentle glide, not a dash */
  .pawnpos.stationed {
    transition: left 0.55s ease, top 0.55s ease;
  }
  /* ---- thought bubbles --------------------------------------------------- */
  .bubble {
    position: absolute;
    left: 50%;
    top: -7px;
    transform: translateX(-30%);
    font-size: 9px;
    line-height: 1;
    padding: 2px 3px;
    border-radius: 6px 6px 6px 1px;
    background: rgba(12, 14, 20, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.14);
    animation: bubblein 0.25s ease-out;
    pointer-events: none;
  }
  .bubble.chat {
    opacity: 0;
    animation: chatblink 13s linear infinite;
  }
  @keyframes bubblein {
    from { opacity: 0; transform: translateX(-30%) translateY(3px) scale(0.6); }
    to { opacity: 1; transform: translateX(-30%) translateY(0) scale(1); }
  }
  @keyframes chatblink {
    0%, 88%, 100% { opacity: 0; }
    89%, 97% { opacity: 1; }
  }
  /* ---- construction moment ----------------------------------------------- */
  .room.fresh :global(.scene) {
    animation: wipein 1.4s ease-out 0.9s backwards;
  }
  .scaffold {
    position: absolute;
    inset: 0;
    z-index: 5;
    border-radius: inherit;
    background:
      repeating-linear-gradient(45deg, transparent 0 14px, rgba(120, 96, 60, 0.55) 14px 17px),
      repeating-linear-gradient(-45deg, transparent 0 14px, rgba(90, 72, 45, 0.45) 14px 17px),
      rgba(14, 14, 20, 0.55);
    animation: scaffout 0.6s ease 1.9s forwards;
    pointer-events: none;
  }
  .dust {
    position: absolute;
    bottom: 8%;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(170, 160, 145, 0.55), transparent 70%);
    z-index: 5;
    animation: dustpuff 1.1s ease-out 2;
    pointer-events: none;
  }
  .dust.d1 { left: 18%; }
  .dust.d2 { left: 62%; animation-delay: 0.45s; }
  @keyframes wipein {
    from { clip-path: inset(0 100% 0 0); }
    to { clip-path: inset(0 0 0 0); }
  }
  @keyframes scaffout {
    to { opacity: 0; }
  }
  @keyframes dustpuff {
    0% { opacity: 0; transform: translateY(4px) scale(0.5); }
    35% { opacity: 0.8; }
    100% { opacity: 0; transform: translateY(-10px) scale(1.6); }
  }
</style>
